import type { Locale } from "@/i18n/languages";
import type { LabelName } from "./labels";

/**
 * The labels printed on an invoice, per language, applied when a customer with an invoice
 * language is picked. English equals the signup defaults (server/pb_hooks/lib/invoices.js).
 * Machine-assisted translations: have a native speaker review a language before relying on it.
 */
export const LABEL_PRESETS: Record<Locale, Record<LabelName, string>> = {
  en: {
    documentTitleLabel: "Invoice",
    invoiceNumberLabel: "Invoice Nr.",
    senderAddressLabel: "From:",
    recipientAddressLabel: "To:",
    issueDateLabel: "Issue date:",
    dueDateLabel: "Due date:",
    fromAndToDateLabel: "Timeframe:",
    itemTitleLabel: "Item",
    itemQuantityLabel: "Quantity",
    itemUnitPriceLabel: "Unit price",
    itemLineTotalLabel: "Line total",
    subTotalLabel: "Sub total",
    discountLabel: "Discount",
    taxLabel: "Tax",
    totalLabel: "Total",
    footNoteLeftLabel: "Payment",
    footNoteRightLabel: "Contact",
  },
  de: {
    documentTitleLabel: "Rechnung",
    invoiceNumberLabel: "Rechnungsnr.",
    senderAddressLabel: "Von:",
    recipientAddressLabel: "An:",
    issueDateLabel: "Rechnungsdatum:",
    dueDateLabel: "Fällig am:",
    fromAndToDateLabel: "Leistungszeitraum:",
    itemTitleLabel: "Leistung",
    itemQuantityLabel: "Menge",
    itemUnitPriceLabel: "Einzelpreis",
    itemLineTotalLabel: "Gesamt",
    subTotalLabel: "Zwischensumme",
    discountLabel: "Rabatt",
    taxLabel: "USt.",
    totalLabel: "Gesamtbetrag",
    footNoteLeftLabel: "Zahlung",
    footNoteRightLabel: "Kontakt",
  },
  fr: {
    documentTitleLabel: "Facture",
    invoiceNumberLabel: "Facture n°",
    senderAddressLabel: "De :",
    recipientAddressLabel: "À :",
    issueDateLabel: "Date d'émission :",
    dueDateLabel: "Date d'échéance :",
    fromAndToDateLabel: "Période de prestation :",
    itemTitleLabel: "Désignation",
    itemQuantityLabel: "Quantité",
    itemUnitPriceLabel: "Prix unitaire",
    itemLineTotalLabel: "Montant",
    subTotalLabel: "Sous-total",
    discountLabel: "Remise",
    taxLabel: "TVA",
    totalLabel: "Total",
    footNoteLeftLabel: "Paiement",
    footNoteRightLabel: "Contact",
  },
  es: {
    documentTitleLabel: "Factura",
    invoiceNumberLabel: "N.º de factura",
    senderAddressLabel: "De:",
    recipientAddressLabel: "Para:",
    issueDateLabel: "Fecha de emisión:",
    dueDateLabel: "Fecha de vencimiento:",
    fromAndToDateLabel: "Período de servicio:",
    itemTitleLabel: "Concepto",
    itemQuantityLabel: "Cantidad",
    itemUnitPriceLabel: "Precio unitario",
    itemLineTotalLabel: "Importe",
    subTotalLabel: "Subtotal",
    discountLabel: "Descuento",
    taxLabel: "IVA",
    totalLabel: "Total",
    footNoteLeftLabel: "Pago",
    footNoteRightLabel: "Contacto",
  },
  it: {
    documentTitleLabel: "Fattura",
    invoiceNumberLabel: "Fattura n.",
    senderAddressLabel: "Da:",
    recipientAddressLabel: "A:",
    issueDateLabel: "Data:",
    dueDateLabel: "Scadenza:",
    fromAndToDateLabel: "Periodo di riferimento:",
    itemTitleLabel: "Descrizione",
    itemQuantityLabel: "Quantità",
    itemUnitPriceLabel: "Prezzo unitario",
    itemLineTotalLabel: "Importo",
    subTotalLabel: "Imponibile",
    discountLabel: "Sconto",
    taxLabel: "IVA",
    totalLabel: "Totale",
    footNoteLeftLabel: "Pagamento",
    footNoteRightLabel: "Contatti",
  },
  nl: {
    documentTitleLabel: "Factuur",
    invoiceNumberLabel: "Factuurnr.",
    senderAddressLabel: "Van:",
    recipientAddressLabel: "Aan:",
    issueDateLabel: "Factuurdatum:",
    dueDateLabel: "Vervaldatum:",
    fromAndToDateLabel: "Leveringsperiode:",
    itemTitleLabel: "Omschrijving",
    itemQuantityLabel: "Aantal",
    itemUnitPriceLabel: "Prijs per stuk",
    itemLineTotalLabel: "Bedrag",
    subTotalLabel: "Subtotaal",
    discountLabel: "Korting",
    taxLabel: "Btw",
    totalLabel: "Totaal",
    footNoteLeftLabel: "Betaling",
    footNoteRightLabel: "Contact",
  },
  pt: {
    documentTitleLabel: "Fatura",
    invoiceNumberLabel: "Fatura n.º",
    senderAddressLabel: "De:",
    recipientAddressLabel: "Para:",
    issueDateLabel: "Data de emissão:",
    dueDateLabel: "Data de vencimento:",
    fromAndToDateLabel: "Período de prestação:",
    itemTitleLabel: "Descrição",
    itemQuantityLabel: "Quantidade",
    itemUnitPriceLabel: "Preço unitário",
    itemLineTotalLabel: "Total",
    subTotalLabel: "Subtotal",
    discountLabel: "Desconto",
    taxLabel: "IVA",
    totalLabel: "Total",
    footNoteLeftLabel: "Pagamento",
    footNoteRightLabel: "Contacto",
  },
  pl: {
    documentTitleLabel: "Faktura",
    invoiceNumberLabel: "Nr faktury",
    senderAddressLabel: "Sprzedawca:",
    recipientAddressLabel: "Nabywca:",
    issueDateLabel: "Data wystawienia:",
    dueDateLabel: "Termin płatności:",
    fromAndToDateLabel: "Okres świadczenia usług:",
    itemTitleLabel: "Nazwa towaru lub usługi",
    itemQuantityLabel: "Ilość",
    itemUnitPriceLabel: "Cena jedn.",
    itemLineTotalLabel: "Wartość",
    subTotalLabel: "Suma częściowa",
    discountLabel: "Rabat",
    taxLabel: "VAT",
    totalLabel: "Do zapłaty",
    footNoteLeftLabel: "Płatność",
    footNoteRightLabel: "Kontakt",
  },
  ro: {
    documentTitleLabel: "Factură",
    invoiceNumberLabel: "Nr. factură",
    senderAddressLabel: "Furnizor:",
    recipientAddressLabel: "Client:",
    issueDateLabel: "Data emiterii:",
    dueDateLabel: "Data scadenței:",
    fromAndToDateLabel: "Perioada prestării:",
    itemTitleLabel: "Denumire",
    itemQuantityLabel: "Cantitate",
    itemUnitPriceLabel: "Preț unitar",
    itemLineTotalLabel: "Valoare",
    subTotalLabel: "Subtotal",
    discountLabel: "Reducere",
    taxLabel: "TVA",
    totalLabel: "Total de plată",
    footNoteLeftLabel: "Plată",
    footNoteRightLabel: "Contact",
  },
  cs: {
    documentTitleLabel: "Faktura",
    invoiceNumberLabel: "Číslo faktury",
    senderAddressLabel: "Dodavatel:",
    recipientAddressLabel: "Odběratel:",
    issueDateLabel: "Datum vystavení:",
    dueDateLabel: "Datum splatnosti:",
    fromAndToDateLabel: "Období plnění:",
    itemTitleLabel: "Položka",
    itemQuantityLabel: "Množství",
    itemUnitPriceLabel: "Cena za jednotku",
    itemLineTotalLabel: "Celkem",
    subTotalLabel: "Mezisoučet",
    discountLabel: "Sleva",
    taxLabel: "DPH",
    totalLabel: "Celkem k úhradě",
    footNoteLeftLabel: "Platba",
    footNoteRightLabel: "Kontakt",
  },
  hu: {
    documentTitleLabel: "Számla",
    invoiceNumberLabel: "Számlaszám",
    senderAddressLabel: "Eladó:",
    recipientAddressLabel: "Vevő:",
    issueDateLabel: "Kiállítás dátuma:",
    dueDateLabel: "Fizetési határidő:",
    fromAndToDateLabel: "Teljesítési időszak:",
    itemTitleLabel: "Megnevezés",
    itemQuantityLabel: "Mennyiség",
    itemUnitPriceLabel: "Egységár",
    itemLineTotalLabel: "Érték",
    subTotalLabel: "Részösszeg",
    discountLabel: "Kedvezmény",
    taxLabel: "ÁFA",
    totalLabel: "Végösszeg",
    footNoteLeftLabel: "Fizetés",
    footNoteRightLabel: "Kapcsolat",
  },
  sv: {
    documentTitleLabel: "Faktura",
    invoiceNumberLabel: "Fakturanr",
    senderAddressLabel: "Från:",
    recipientAddressLabel: "Till:",
    issueDateLabel: "Fakturadatum:",
    dueDateLabel: "Förfallodatum:",
    fromAndToDateLabel: "Leveransperiod:",
    itemTitleLabel: "Artikel",
    itemQuantityLabel: "Antal",
    itemUnitPriceLabel: "À-pris",
    itemLineTotalLabel: "Belopp",
    subTotalLabel: "Delsumma",
    discountLabel: "Rabatt",
    taxLabel: "Moms",
    totalLabel: "Att betala",
    footNoteLeftLabel: "Betalning",
    footNoteRightLabel: "Kontakt",
  },
  da: {
    documentTitleLabel: "Faktura",
    invoiceNumberLabel: "Fakturanr.",
    senderAddressLabel: "Fra:",
    recipientAddressLabel: "Til:",
    issueDateLabel: "Fakturadato:",
    dueDateLabel: "Forfaldsdato:",
    fromAndToDateLabel: "Leveringsperiode:",
    itemTitleLabel: "Vare",
    itemQuantityLabel: "Antal",
    itemUnitPriceLabel: "Enhedspris",
    itemLineTotalLabel: "Beløb",
    subTotalLabel: "Subtotal",
    discountLabel: "Rabat",
    taxLabel: "Moms",
    totalLabel: "I alt",
    footNoteLeftLabel: "Betaling",
    footNoteRightLabel: "Kontakt",
  },
  nb: {
    documentTitleLabel: "Faktura",
    invoiceNumberLabel: "Fakturanr.",
    senderAddressLabel: "Fra:",
    recipientAddressLabel: "Til:",
    issueDateLabel: "Fakturadato:",
    dueDateLabel: "Forfallsdato:",
    fromAndToDateLabel: "Leveringsperiode:",
    itemTitleLabel: "Vare",
    itemQuantityLabel: "Antall",
    itemUnitPriceLabel: "Enhetspris",
    itemLineTotalLabel: "Beløp",
    subTotalLabel: "Delsum",
    discountLabel: "Rabatt",
    taxLabel: "MVA",
    totalLabel: "Å betale",
    footNoteLeftLabel: "Betaling",
    footNoteRightLabel: "Kontakt",
  },
  fi: {
    documentTitleLabel: "Lasku",
    invoiceNumberLabel: "Laskun nro",
    senderAddressLabel: "Laskuttaja:",
    recipientAddressLabel: "Asiakas:",
    issueDateLabel: "Laskun päiväys:",
    dueDateLabel: "Eräpäivä:",
    fromAndToDateLabel: "Toimitusaika:",
    itemTitleLabel: "Nimike",
    itemQuantityLabel: "Määrä",
    itemUnitPriceLabel: "Yksikköhinta",
    itemLineTotalLabel: "Summa",
    subTotalLabel: "Välisumma",
    discountLabel: "Alennus",
    taxLabel: "ALV",
    totalLabel: "Yhteensä",
    footNoteLeftLabel: "Maksutiedot",
    footNoteRightLabel: "Yhteystiedot",
  },
  is: {
    documentTitleLabel: "Reikningur",
    invoiceNumberLabel: "Reikningsnr.",
    senderAddressLabel: "Seljandi:",
    recipientAddressLabel: "Kaupandi:",
    issueDateLabel: "Útgáfudagur:",
    dueDateLabel: "Gjalddagi:",
    fromAndToDateLabel: "Tímabil:",
    itemTitleLabel: "Lýsing",
    itemQuantityLabel: "Magn",
    itemUnitPriceLabel: "Einingaverð",
    itemLineTotalLabel: "Upphæð",
    subTotalLabel: "Millisamtala",
    discountLabel: "Afsláttur",
    taxLabel: "VSK",
    totalLabel: "Samtals",
    footNoteLeftLabel: "Greiðsla",
    footNoteRightLabel: "Samskipti",
  },
  et: {
    documentTitleLabel: "Arve",
    invoiceNumberLabel: "Arve nr",
    senderAddressLabel: "Arve esitaja:",
    recipientAddressLabel: "Arve saaja:",
    issueDateLabel: "Arve kuupäev:",
    dueDateLabel: "Maksetähtaeg:",
    fromAndToDateLabel: "Teenuse periood:",
    itemTitleLabel: "Nimetus",
    itemQuantityLabel: "Kogus",
    itemUnitPriceLabel: "Ühiku hind",
    itemLineTotalLabel: "Summa",
    subTotalLabel: "Vahesumma",
    discountLabel: "Allahindlus",
    taxLabel: "Käibemaks",
    totalLabel: "Kokku tasuda",
    footNoteLeftLabel: "Makseandmed",
    footNoteRightLabel: "Kontakt",
  },
  lv: {
    documentTitleLabel: "Rēķins",
    invoiceNumberLabel: "Rēķina nr.",
    senderAddressLabel: "Piegādātājs:",
    recipientAddressLabel: "Saņēmējs:",
    issueDateLabel: "Izrakstīšanas datums:",
    dueDateLabel: "Apmaksas termiņš:",
    fromAndToDateLabel: "Pakalpojuma periods:",
    itemTitleLabel: "Nosaukums",
    itemQuantityLabel: "Daudzums",
    itemUnitPriceLabel: "Vienības cena",
    itemLineTotalLabel: "Summa",
    subTotalLabel: "Starpsumma",
    discountLabel: "Atlaide",
    taxLabel: "PVN",
    totalLabel: "Kopā apmaksai",
    footNoteLeftLabel: "Maksājuma rekvizīti",
    footNoteRightLabel: "Kontakti",
  },
  lt: {
    documentTitleLabel: "Sąskaita faktūra",
    invoiceNumberLabel: "Sąskaitos Nr.",
    senderAddressLabel: "Pardavėjas:",
    recipientAddressLabel: "Pirkėjas:",
    issueDateLabel: "Išrašymo data:",
    dueDateLabel: "Apmokėti iki:",
    fromAndToDateLabel: "Paslaugų laikotarpis:",
    itemTitleLabel: "Pavadinimas",
    itemQuantityLabel: "Kiekis",
    itemUnitPriceLabel: "Vieneto kaina",
    itemLineTotalLabel: "Suma",
    subTotalLabel: "Tarpinė suma",
    discountLabel: "Nuolaida",
    taxLabel: "PVM",
    totalLabel: "Iš viso mokėti",
    footNoteLeftLabel: "Mokėjimo rekvizitai",
    footNoteRightLabel: "Kontaktai",
  },
  tr: {
    documentTitleLabel: "Fatura",
    invoiceNumberLabel: "Fatura No.",
    senderAddressLabel: "Satıcı:",
    recipientAddressLabel: "Alıcı:",
    issueDateLabel: "Düzenleme tarihi:",
    dueDateLabel: "Vade tarihi:",
    fromAndToDateLabel: "Hizmet dönemi:",
    itemTitleLabel: "Mal/Hizmet",
    itemQuantityLabel: "Miktar",
    itemUnitPriceLabel: "Birim fiyat",
    itemLineTotalLabel: "Tutar",
    subTotalLabel: "Ara toplam",
    discountLabel: "İndirim",
    taxLabel: "KDV",
    totalLabel: "Genel toplam",
    footNoteLeftLabel: "Ödeme",
    footNoteRightLabel: "İletişim",
  },
};

/** Printed in front of the customer's VAT ID in the recipient block. */
/**
 * North American English on an invoice (US and Canadian sellers, NORTH_AMERICAN in
 * country-presets.ts): what differs from LABEL_PRESETS.en. "Bill to:" also marks a document as
 * North American (`isNorthAmerican` in quote-labels.ts), for the email text.
 */
export const US_LABELS = {
  invoiceNumberLabel: "Invoice #",
  recipientAddressLabel: "Bill to:",
  issueDateLabel: "Invoice date:",
  fromAndToDateLabel: "Service period:",
  subTotalLabel: "Subtotal",
  totalLabel: "Amount due",
} as const satisfies Partial<Record<LabelName, string>>;

export const VAT_ID_LABEL: Record<Locale, string> = {
  en: "VAT ID",
  de: "USt-IdNr.",
  fr: "N° TVA",
  es: "NIF-IVA",
  it: "P. IVA",
  nl: "Btw-id",
  pt: "NIF",
  pl: "NIP",
  ro: "Cod TVA",
  cs: "DIČ",
  hu: "Adószám",
  sv: "Momsreg.nr",
  da: "Momsnr.",
  nb: "MVA-nr.",
  fi: "ALV-tunnus",
  is: "VSK-númer",
  et: "KMKR nr",
  lv: "PVN reģ. nr.",
  lt: "PVM mokėtojo kodas",
  tr: "VKN",
};

/**
 * What changes on a quote: title, number, the date ("valid until" instead of "due") and the left
 * note ("terms" instead of "payment"). Every other label is the same as on an invoice.
 */
export const QUOTE_LABEL_NAMES = [
  "documentTitleLabel",
  "invoiceNumberLabel",
  "dueDateLabel",
  "footNoteLeftLabel",
] as const;
export type QuoteLabelName = (typeof QUOTE_LABEL_NAMES)[number];

export const QUOTE_LABEL_PRESETS: Record<Locale, Record<QuoteLabelName, string>> = {
  en: {
    documentTitleLabel: "Quote",
    invoiceNumberLabel: "Quote Nr.",
    dueDateLabel: "Valid until:",
    footNoteLeftLabel: "Terms",
  },
  de: {
    documentTitleLabel: "Angebot",
    invoiceNumberLabel: "Angebotsnr.",
    dueDateLabel: "Gültig bis:",
    footNoteLeftLabel: "Konditionen",
  },
  fr: {
    documentTitleLabel: "Devis",
    invoiceNumberLabel: "Devis n°",
    dueDateLabel: "Valable jusqu'au :",
    footNoteLeftLabel: "Conditions",
  },
  es: {
    documentTitleLabel: "Presupuesto",
    invoiceNumberLabel: "N.º de presupuesto",
    dueDateLabel: "Válido hasta:",
    footNoteLeftLabel: "Condiciones",
  },
  it: {
    documentTitleLabel: "Preventivo",
    invoiceNumberLabel: "Preventivo n.",
    dueDateLabel: "Valido fino al:",
    footNoteLeftLabel: "Condizioni",
  },
  nl: {
    documentTitleLabel: "Offerte",
    invoiceNumberLabel: "Offertenr.",
    dueDateLabel: "Geldig tot:",
    footNoteLeftLabel: "Voorwaarden",
  },
  pt: {
    documentTitleLabel: "Orçamento",
    invoiceNumberLabel: "Orçamento n.º",
    dueDateLabel: "Válido até:",
    footNoteLeftLabel: "Condições",
  },
  pl: {
    documentTitleLabel: "Oferta",
    invoiceNumberLabel: "Nr oferty",
    dueDateLabel: "Ważna do:",
    footNoteLeftLabel: "Warunki",
  },
  ro: {
    documentTitleLabel: "Ofertă",
    invoiceNumberLabel: "Nr. ofertă",
    dueDateLabel: "Valabilă până la:",
    footNoteLeftLabel: "Condiții",
  },
  cs: {
    documentTitleLabel: "Nabídka",
    invoiceNumberLabel: "Číslo nabídky",
    dueDateLabel: "Platnost do:",
    footNoteLeftLabel: "Podmínky",
  },
  hu: {
    documentTitleLabel: "Árajánlat",
    invoiceNumberLabel: "Ajánlatszám",
    dueDateLabel: "Érvényes eddig:",
    footNoteLeftLabel: "Feltételek",
  },
  sv: {
    documentTitleLabel: "Offert",
    invoiceNumberLabel: "Offertnr",
    dueDateLabel: "Giltig till:",
    footNoteLeftLabel: "Villkor",
  },
  da: {
    documentTitleLabel: "Tilbud",
    invoiceNumberLabel: "Tilbudsnr.",
    dueDateLabel: "Gyldigt til:",
    footNoteLeftLabel: "Betingelser",
  },
  nb: {
    documentTitleLabel: "Tilbud",
    invoiceNumberLabel: "Tilbudsnr.",
    dueDateLabel: "Gyldig til:",
    footNoteLeftLabel: "Vilkår",
  },
  fi: {
    documentTitleLabel: "Tarjous",
    invoiceNumberLabel: "Tarjouksen nro",
    dueDateLabel: "Voimassa:",
    footNoteLeftLabel: "Ehdot",
  },
  is: {
    documentTitleLabel: "Tilboð",
    invoiceNumberLabel: "Tilboðsnr.",
    dueDateLabel: "Gildir til:",
    footNoteLeftLabel: "Skilmálar",
  },
  et: {
    documentTitleLabel: "Hinnapakkumine",
    invoiceNumberLabel: "Pakkumise nr",
    dueDateLabel: "Kehtib kuni:",
    footNoteLeftLabel: "Tingimused",
  },
  lv: {
    documentTitleLabel: "Piedāvājums",
    invoiceNumberLabel: "Piedāvājuma nr.",
    dueDateLabel: "Derīgs līdz:",
    footNoteLeftLabel: "Nosacījumi",
  },
  lt: {
    documentTitleLabel: "Komercinis pasiūlymas",
    invoiceNumberLabel: "Pasiūlymo Nr.",
    dueDateLabel: "Galioja iki:",
    footNoteLeftLabel: "Sąlygos",
  },
  tr: {
    documentTitleLabel: "Teklif",
    invoiceNumberLabel: "Teklif No.",
    dueDateLabel: "Geçerlilik tarihi:",
    footNoteLeftLabel: "Koşullar",
  },
};

/**
 * A credit note's title and number label, and the line that names the invoice it credits
 * (`{{number}}`, `{{date}}`), per language. Machine-assisted like the rest.
 */
export const CREDIT_NOTE_LABEL_NAMES = ["documentTitleLabel", "invoiceNumberLabel"] as const;
export type CreditNoteLabelName = (typeof CREDIT_NOTE_LABEL_NAMES)[number];

export const CREDIT_NOTE_LABEL_PRESETS: Record<
  Locale,
  Record<CreditNoteLabelName, string> & { reference: string }
> = {
  en: {
    documentTitleLabel: "Credit note",
    invoiceNumberLabel: "Credit note Nr.",
    reference: "Credit note for invoice {{number}} of {{date}}",
  },
  de: {
    documentTitleLabel: "Rechnungskorrektur",
    invoiceNumberLabel: "Korrektur-Nr.",
    reference: "Korrektur zur Rechnung {{number}} vom {{date}}",
  },
  fr: {
    documentTitleLabel: "Avoir",
    invoiceNumberLabel: "Avoir n°",
    reference: "Avoir sur la facture {{number}} du {{date}}",
  },
  es: {
    documentTitleLabel: "Factura rectificativa",
    invoiceNumberLabel: "N.º de rectificativa",
    reference: "Rectifica la factura {{number}} del {{date}}",
  },
  it: {
    documentTitleLabel: "Nota di credito",
    invoiceNumberLabel: "Nota di credito n.",
    reference: "Nota di credito relativa alla fattura {{number}} del {{date}}",
  },
  nl: {
    documentTitleLabel: "Creditnota",
    invoiceNumberLabel: "Creditnotanr.",
    reference: "Creditnota bij factuur {{number}} van {{date}}",
  },
  pt: {
    documentTitleLabel: "Nota de crédito",
    invoiceNumberLabel: "Nota de crédito n.º",
    reference: "Nota de crédito relativa à fatura {{number}} de {{date}}",
  },
  pl: {
    documentTitleLabel: "Faktura korygująca",
    invoiceNumberLabel: "Nr korekty",
    reference: "Korekta faktury {{number}} z dnia {{date}}",
  },
  cs: {
    documentTitleLabel: "Dobropis",
    invoiceNumberLabel: "Číslo dobropisu",
    reference: "Dobropis k faktuře {{number}} ze dne {{date}}",
  },
  hu: {
    documentTitleLabel: "Jóváíró számla",
    invoiceNumberLabel: "Számlaszám",
    reference: "A(z) {{number}} számú, {{date}} keltű számla helyesbítése",
  },
  ro: {
    documentTitleLabel: "Notă de credit",
    invoiceNumberLabel: "Nr. notă de credit",
    reference: "Notă de credit pentru factura {{number}} din {{date}}",
  },
  sv: {
    documentTitleLabel: "Kreditfaktura",
    invoiceNumberLabel: "Kreditfakturanr",
    reference: "Kreditering av faktura {{number}} från {{date}}",
  },
  da: {
    documentTitleLabel: "Kreditnota",
    invoiceNumberLabel: "Kreditnotanr.",
    reference: "Kreditnota til faktura {{number}} af {{date}}",
  },
  fi: {
    documentTitleLabel: "Hyvityslasku",
    invoiceNumberLabel: "Hyvityslaskun nro",
    reference: "Hyvitys laskulle {{number}} ({{date}})",
  },
  nb: {
    documentTitleLabel: "Kreditnota",
    invoiceNumberLabel: "Kreditnotanr.",
    reference: "Kreditnota for faktura {{number}} datert {{date}}",
  },
  is: {
    documentTitleLabel: "Kreditreikningur",
    invoiceNumberLabel: "Kreditreikningur nr.",
    reference: "Kreditreikningur vegna reiknings {{number}} frá {{date}}",
  },
  et: {
    documentTitleLabel: "Kreeditarve",
    invoiceNumberLabel: "Kreeditarve nr",
    reference: "Kreeditarve arvele {{number}} ({{date}})",
  },
  lv: {
    documentTitleLabel: "Kredītrēķins",
    invoiceNumberLabel: "Kredītrēķina Nr.",
    reference: "Kredītrēķins rēķinam {{number}} no {{date}}",
  },
  lt: {
    documentTitleLabel: "Kreditinė sąskaita",
    invoiceNumberLabel: "Kreditinės sąskaitos Nr.",
    reference: "Kreditinė sąskaita sąskaitai {{number}}, išrašytai {{date}}",
  },
  tr: {
    documentTitleLabel: "İade faturası",
    invoiceNumberLabel: "İade fatura no",
    reference: "{{date}} tarihli {{number}} numaralı faturaya ilişkin iade",
  },
};
